import { useCallback } from 'react';
import { useDialogStore } from '@/stores/dialog-store';
import { useToastStore } from '@/stores/toast-store';
import { ConfirmDialog } from './ConfirmDialog';

/**
 * Singleton host for the imperative `showConfirm()` API. Renders the existing
 * ConfirmDialog when the dialog-store says it's open; runs the caller's
 * onConfirm() and closes on success, keeps the dialog open on error so the
 * toast shows a real message and the user can retry.
 *
 * Mount once at the app root (AppShell / MobileShell).
 */
export function ConfirmDialogHost() {
  const confirm = useDialogStore((s) => s.confirm);
  const setLoading = useDialogStore((s) => s.setConfirmLoading);
  const close = useDialogStore((s) => s.closeConfirm);
  const addToast = useToastStore((s) => s.addToast);

  const handleConfirm = useCallback(async () => {
    if (!confirm.onConfirm) return;
    setLoading(true);
    try {
      await confirm.onConfirm();
      close(true);
    } catch (err: unknown) {
      setLoading(false);
      const message = err instanceof Error ? err.message : 'Action failed';
      addToast('error', message);
    }
  }, [confirm.onConfirm, setLoading, close, addToast]);

  if (!confirm.open) return null;

  return (
    <ConfirmDialog
      title={confirm.title}
      message={confirm.message}
      confirmText={confirm.confirmText}
      confirmLabel={confirm.confirmLabel}
      danger={confirm.danger}
      loading={confirm.loading}
      onConfirm={handleConfirm}
      onCancel={() => close(false)}
    />
  );
}
